// src/App.js
import React, { useMemo, useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { ChatProvider } from './ChatContext';
import Chatbot from './Chatbot';
import Navbar from './Navbar';

import Healthcare from './pages/Healthcare';
import BFSI from './pages/BFSI';
import Hitech from './pages/Hitech';
import Retail from './pages/Retail';
import Logistics from './pages/Logistics';
import RealEstate from './pages/RealEstate';

import FloatContactButtons from './pages/FloatContactButtons';

// Strings context
import { StringsProvider, useStrings } from './strings/StringsContext';

function useAgentOnly() {
  const location = useLocation();
  return useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const a = sp.get('agent') || '';
    return a === '1' || a === '2';
  }, [location.search]);
}

function AppShell() {
  const { strings, agentName } = useStrings(); // ✅ declare ONCE
  const location = useLocation();
  const agentOnly = useAgentOnly();
  const [chatState, setChatState] = useState({ isOpen: false, isExpanded: false });

  // Update title + favicon when strings change
  useEffect(() => {
    const appName = strings?.brand?.appName || agentName || 'SakCare Agent';
    document.title = appName;

    const iconHref = strings?.brand?.logoSrc || '/bot-avatar.png';
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = iconHref;
  }, [strings, agentName]);

  useEffect(() => {
    const handler = () => setChatState((s) => ({ ...s, isOpen: true, isExpanded: true }));
    window.addEventListener('open-chatbot', handler);
    return () => window.removeEventListener('open-chatbot', handler);
  }, []);

  const showNavbar = !agentOnly;
  const showPages = !agentOnly;
  const showChatbot = true;

  return (
    <ChatProvider>
      {showNavbar && <Navbar />}

      {showPages && (
        <Routes>
          <Route path="/" element={<BFSI setChatState={setChatState} />} />
          <Route path="/healthcare" element={<Healthcare setChatState={setChatState} />} />
          <Route path="/bfsi" element={<BFSI setChatState={setChatState} />} />
          <Route path="/logistics" element={<Logistics setChatState={setChatState} />} />
          <Route path="/hi-tech" element={<Hitech setChatState={setChatState} />} />
          <Route path="/digital-commerce" element={<Retail setChatState={setChatState} />} />
          <Route path="/realestate" element={<RealEstate setChatState={setChatState} />} />
          <Route
            path="*"
            element={<Navigate to={{ pathname: '/healthcare', search: location.search }} replace />}
          />
        </Routes>
      )}

      {showChatbot && (
        <Chatbot
          isOpen={chatState.isOpen}
          isExpanded={chatState.isExpanded}
          setChatState={setChatState}
        />
      )}

      {!agentOnly && (
        <FloatContactButtons
          mailMode="outlook-web"
          mailHref="sakcare@finance.ceptes.com"
          mailSubject="Emergency request for transaction dispute"
          mailBody={`Hello,
This is an emergency request related to my recent trasaction, please assist.
Thanks,`}
          phoneHref="tel:+12248033220"
          whatsappHref="https://wa.me/15550536332?text=Hello"
        />
      )}
    </ChatProvider>
  );
}

export default function App() {
  return (
    <StringsProvider>
      <AppShell />
    </StringsProvider>
  );
}
